/* global trapFocus, removeTrapFocus */

if (!customElements.get('hotspots-image')) {
  class HotspotsImage extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', this.handleClick.bind(this));
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.clickOutsideHandler) {
        document.removeEventListener('click', this.clickOutsideHandler);
      }

      if (this.keyUpHandler) {
        document.removeEventListener('keyup', this.keyUpHandler);
      }

      if (this.transitionHandler) {
        this.removeEventListener('transitionend', this.transitionHandler);
      }
    }

    /**
     * Handles clicks on hotspot buttons.
     * Uses closest() so clicks on the plus icon, SVG, path,
     * number, or other children of the button work correctly.
     */
    handleClick(evt) {
      const button = evt.target.closest('.hotspot__btn');
      if (!button || !this.contains(button)) return;

      const hotspot = button.closest('.hotspot');
      if (!hotspot) return;

      if (hotspot === this.activeHotspot) {
        this.closeActiveHotspot(evt, true);
      } else {
        if (this.activeHotspot) {
          this.closeActiveHotspot(evt, false);
        }

        this.activateHotspot(hotspot);
      }
    }

    /**
     * Handles clicks outside the active hotspot.
     */
    handleClickOutside(evt) {
      if (!this.activeHotspot) return;

      if (
        evt.target.closest('.hotspot__btn') ||
        evt.target.closest('.hotspot__card')
      ) {
        return;
      }

      this.closeActiveHotspot(evt, true);
    }

    /**
     * Closes the active hotspot when Escape is pressed.
     */
    handleKeyup(evt) {
      if (evt.key !== 'Escape' || !this.activeHotspot) return;

      evt.preventDefault();
      this.closeActiveHotspot(evt, true);
    }

    /**
     * Cleans up after the hotspot close transition.
     */
    handleTransitionEnd(evt) {
      if (this.activeHotspot || evt.propertyName !== 'opacity') return;

      this.classList.add('overflow-hidden');

      if (this.activeCard) {
        this.activeCard.style = null;
      }

      this.activeCard = null;

      this.removeEventListener(
        'transitionend',
        this.transitionHandler
      );
    }

    /**
     * Opens a hotspot card.
     */
    activateHotspot(hotspot) {
      this.activeCard = hotspot.querySelector('.hotspot__card');
      this.activeButton = hotspot.querySelector('.hotspot__btn');

      if (!this.activeCard || !this.activeButton) return;

      this.checkCardPosition();

      this.classList.remove('overflow-hidden');

      this.setHotspotActiveState(hotspot, true);

      /*
       * Keep keyboard focus inside the active hotspot
       * using the theme's existing focus utility.
       */
      trapFocus(hotspot);

      this.clickOutsideHandler =
        this.clickOutsideHandler ||
        this.handleClickOutside.bind(this);

      this.keyUpHandler =
        this.keyUpHandler ||
        this.handleKeyup.bind(this);

      this.resizeHandler =
        this.resizeHandler ||
        this.closeActiveHotspot.bind(this);

      document.addEventListener(
        'click',
        this.clickOutsideHandler
      );

      document.addEventListener(
        'keyup',
        this.keyUpHandler
      );

      window.addEventListener(
        'on:debounced-resize',
        this.resizeHandler
      );
    }

    /**
     * Closes the active hotspot.
     *
     * @param {Event} evt
     * @param {boolean} returnFocus
     * Return keyboard focus to the hotspot button when appropriate.
     */
    closeActiveHotspot(evt, returnFocus = false) {
      if (!this.activeHotspot) return;

      const buttonToRestore =
        this.activeHotspot.querySelector('.hotspot__btn');

      if (evt.type !== 'on:debounced-resize') {
        this.transitionHandler =
          this.transitionHandler ||
          this.handleTransitionEnd.bind(this);

        this.addEventListener(
          'transitionend',
          this.transitionHandler
        );
      }

      this.setHotspotActiveState(
        this.activeHotspot,
        false
      );

      removeTrapFocus();

      if (evt.type === 'on:debounced-resize') {
        this.classList.add('overflow-hidden');

        if (this.activeCard) {
          this.activeCard.style = null;
        }

        this.activeCard = null;
      }

      document.removeEventListener(
        'click',
        this.clickOutsideHandler
      );

      document.removeEventListener(
        'keyup',
        this.keyUpHandler
      );

      window.removeEventListener(
        'on:debounced-resize',
        this.resizeHandler
      );

      /*
       * When Escape or an outside click closes the card,
       * return keyboard focus to the hotspot trigger.
       */
      if (
        returnFocus &&
        buttonToRestore &&
        evt.type !== 'on:debounced-resize'
      ) {
        window.requestAnimationFrame(() => {
          buttonToRestore.focus();
        });
      }

      this.activeButton = null;
    }

    /**
     * Keeps the hotspot card within the image and viewport.
     */
    checkCardPosition() {
      if (!this.activeCard) return;

      let cardRect;

      /*
       * Card prefers to be centered on mobile,
       * while remaining inside the image bounds.
       */
      if (!theme.mediaMatches.md) {
        const buttonRect =
          this.activeCard.parentElement.getBoundingClientRect();

        const imageRect =
          this.getBoundingClientRect();

        const rightOffset =
          this.activeCard.clientWidth / 2;

        this.activeCard.style.right =
          `calc(50% - ${rightOffset}px)`;

        cardRect =
          this.activeCard.getBoundingClientRect();

        const naturalLeft =
          buttonRect.left +
          buttonRect.width / 2 -
          cardRect.width / 2;

        if (naturalLeft < 10) {
          this.activeCard.style.right =
            `calc(50% - ${
              rightOffset - naturalLeft
            }px - var(--gutter))`;
        } else {
          const naturalRight =
            buttonRect.left +
            buttonRect.width / 2 +
            cardRect.width / 2;

          if (naturalRight > imageRect.width - 10) {
            this.activeCard.style.right =
              `calc(50% - ${
                rightOffset -
                (naturalRight - imageRect.width)
              }px + var(--gutter))`;
          }
        }
      } else {
        cardRect =
          this.activeCard.getBoundingClientRect();
      }

      /*
       * Move the card above the hotspot when it would
       * otherwise extend below the viewport.
       */
      if (
        cardRect.bottom >
        document.documentElement.clientHeight
      ) {
        this.activeCard.style.top = 'auto';

        this.activeCard.style.bottom =
          'calc(100% + 12px)';
      }
    }

    /**
     * Updates visual and accessibility state.
     */
    setHotspotActiveState(hotspot, active) {
      hotspot.classList.toggle(
        'is-active',
        active
      );

      const button =
        hotspot.querySelector('.hotspot__btn');

      const card =
        hotspot.querySelector('.hotspot__card');

      if (button) {
        button.setAttribute(
          'aria-expanded',
          active ? 'true' : 'false'
        );
      }

      if (card) {
        card.setAttribute(
          'aria-hidden',
          active ? 'false' : 'true'
        );
      }

      this.activeHotspot =
        active ? hotspot : null;
    }
  }

  customElements.define(
    'hotspots-image',
    HotspotsImage
  );
}